import React, { useRef, useState } from 'react';
import { gql } from '@apollo/client';
import DetailSection from '../DetailSection';
import EmploymentSection from './EmploymentSection';
import styled from 'styled-components';
import { Alert, CardButton, Grid } from '@zillow/constellation';
import { buttonMixin, sectionMixin } from '../utils/viewMoreMixins';
import sortByEndDate from '../utils/sortByEndDate';
import TagType from '../../../../../../constants/tagType';
import { ErrorBoundary } from 'react-error-boundary';
import { CardSpinner } from '../../../../../../components/CardSpinner';
import { useZgGraphqlQuery } from '../../../../../../hooks/useGraphqlQuery';
import useQueryVariables from '../../../../../../hooks/useQueryVariables';
import { getGlobalLogger } from '@zg-rentals/logger-base';

const logger = getGlobalLogger(__filename);

export const QUERY = gql`
  query EmploymentHistory($propertyId: ID!, $applicationId: ID!, $applicantId: ID!, $zgAuthZone: ZgAuthZone!) {
    rentalListing(id: $propertyId) {
      id
      landlordRentalApplication(id: $applicationId) {
        id
        applicant(userId: $applicantId, zgAuthZone: $zgAuthZone) {
          id
          userId
          zgAuthZone
          answers {
            id
            currentIncomes {
              id
              title
              ... on EmploymentIncome {
                startDate
                employer {
                  name
                  phone
                  email
                  organization
                }
              }
            }
            pastJobs {
              id
              title
              startDate
              endDate
              employer {
                name
                email
                phone
                organization
              }
            }
          }
        }
      }
    }
  }
`;

export const NO_EMPLOYMENT_MSG = 'No employment history provided';
export const ERROR_MSG = "Sorry, there was an error loading this applicant's employment history.";

const StyledEmploymentSection = styled(EmploymentSection)`
  ${sectionMixin}
`;

const ViewMoreButton = styled(CardButton)`
  ${buttonMixin}
`;

export default function EmploymentHistory() {
  const [isExpanded, setIsExpanded] = useState(false);
  const employmentRef = useRef();
  const queryVariables = useQueryVariables();
  const heading = 'Employment history';

  const { data, isLoading, isError } = useZgGraphqlQuery(QUERY, queryVariables);

  if (isLoading)
    return (
      <DetailSection heading={heading}>
        <CardSpinner />
      </DetailSection>
    );
  if (isError) return <Alert>{ERROR_MSG}</Alert>;

  const viewMoreOrLess = () => {
    setIsExpanded(!isExpanded);

    if (isExpanded) {
      employmentRef.current.scrollIntoView({
        behavior: 'smooth',
      });
    }
  };

  const currentIncomes = data.rentalListing.landlordRentalApplication.applicant.answers.currentIncomes;
  // not filtering based on typename because type names don't come through in tests
  const employmentIncomes = currentIncomes.filter((currentIncome) => currentIncome.employer);
  const joinedIncomes = [
    ...data.rentalListing.landlordRentalApplication.applicant.answers.pastJobs,
    ...employmentIncomes,
  ].sort(sortByEndDate);

  return (
    <DetailSection id="employment-history" heading={heading} tagType={TagType.selfReported} ref={employmentRef}>
      <ErrorBoundary fallback={null} onError={(error) => logger.warn(error)}>
        {joinedIncomes.length === 0 ? (
          <Alert id="no-employment-alert" body={NO_EMPLOYMENT_MSG} />
        ) : (
          <>
            <Grid
              display="grid"
              rowGap="md"
              columnGap="lg"
              gridTemplateColumns={{
                default: '1fr',
                md: 'repeat(2, minmax(0, 1fr))',
                lg: 'repeat(3, minmax(0, 1fr))',
                xl: 'repeat(4, minmax(0, 1fr))',
              }}
            >
              {joinedIncomes.map((employmentIncome, index) => (
                <StyledEmploymentSection
                  key={`${employmentIncome.title}${employmentIncome.startDate}`}
                  index={index}
                  isExpanded={isExpanded}
                  employmentIncome={employmentIncome}
                />
              ))}
            </Grid>
            <ViewMoreButton
              numItems={joinedIncomes.length}
              isExpanded={isExpanded}
              onClick={viewMoreOrLess}
              id="employment-history-view-more-button"
            >
              View {isExpanded ? 'less' : 'more'}
            </ViewMoreButton>
          </>
        )}
      </ErrorBoundary>
    </DetailSection>
  );
}
